import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  schemaData?: object;
  noIndex?: boolean;
}

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  schemaData,
  noIndex = false
}: SEOProps) {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://buyinzagreb.com';
  const currentUrl = `${siteUrl}${location.pathname}`;

  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    if (description) {
      updateMetaTag('description', description);
      updateMetaTag('title', title || '');
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Robots meta tag
    if (noIndex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow');
    }

    // Canonical URL
    let canonicalElement = document.querySelector('link[rel="canonical"]');
    if (!canonicalElement) {
      canonicalElement = document.createElement('link');
      canonicalElement.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalElement);
    }
    canonicalElement.setAttribute('href', canonical || currentUrl);

    // Open Graph tags
    if (title) {
      updateMetaTag('og:title', title, true);
    }
    if (description) {
      updateMetaTag('og:description', description, true);
    }
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:type', 'website', true);
    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
    }
    updateMetaTag('og:locale', 'hr_HR', true);
    updateMetaTag('og:site_name', 'Buy in Zagreb', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    if (title) {
      updateMetaTag('twitter:title', title);
    }
    if (description) {
      updateMetaTag('twitter:description', description);
    }
    updateMetaTag('twitter:url', currentUrl);
    if (ogImage) {
      updateMetaTag('twitter:image', ogImage);
    }

    // Last modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Schema.org JSON-LD
    if (schemaData) {
      let scriptElement = document.querySelector('script[data-schema="page"]');
      
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'application/ld+json');
        scriptElement.setAttribute('data-schema', 'page');
        document.head.appendChild(scriptElement);
      }
      
      scriptElement.textContent = JSON.stringify(schemaData);
    }

    // Cleanup function
    return () => {
      // Remove page-specific schema when component unmounts
      const schemaElement = document.querySelector('script[data-schema="page"]');
      if (schemaElement) {
        schemaElement.remove();
      }
    };
  }, [title, description, keywords, canonical, currentUrl, ogImage, schemaData, noIndex, siteUrl]);

  return null;
}
